import { faHandshake } from '@fortawesome/free-solid-svg-icons';
import { ListItemText } from '@mui/material';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router';

import { tradingSlice } from '!views/index';
import { menusSlice } from '»components/Menus/_menus.slice';
import {
  getActiveMenuItemSelector,
  getActiveNavigationMenuItemSelector,
  getMainMenu,
  isMenuOpened,
} from '»components/Menus/menus.selectors';
import {
  DrawerMobileStyled,
  FontAwesomeIconMobileStyled,
  LeafListItemButtonMobileStyled,
  LeafListItemTextMobileStyled,
  ListItemIconMobileStyled,
  ListMobileStyled,
  RootListItemButtonMobileStyled,
} from '»components/Menus/Mobile/Menu.mobile.styles';
import { useIsAbleToProceed } from '»utils/customHooks/useIsAbleToProceed';
import { TrlFormattedMessagePlain } from '»utils/i18n';

export const TradingMenuItemMobile = () => {
  const dispatch = useDispatch();

  const activeMenuItem = useSelector(getActiveMenuItemSelector);
  const activeNavigationMenuItem = useSelector(
    getActiveNavigationMenuItemSelector,
  );
  const isOpen = useSelector(isMenuOpened);
  const mainMenu = useSelector(getMainMenu);

  const available = mainMenu.some(
    (menuOption) => menuOption.optionId === 'TRLT_TRADING',
  );
  const availablePrimary = mainMenu.some(
    (menuOption) => menuOption.optionId === 'TRLT_TRADING_PRIMARY',
  );
  const availableStockExchange = mainMenu.some(
    (menuOption) => menuOption.optionId === 'TRLT_TRADING_STOCK_EXCHANGE',
  );
  const availableFunds = mainMenu.some(
    (menuOption) => menuOption.optionId === 'TRLT_TRADING_FUNDS',
  );

  const { isAbleToProceed } = useIsAbleToProceed();

  const OPTION_ID = 'TRADING';
  const LEAF_OPTION_ID1 = 'PRIMARY';
  const LEAF_OPTION_ID2 = 'BOLSA';
  const LEAF_OPTION_ID3 = 'FUNDS';

  const handleClickLeafMenu = (event, menuItem) => {
    isAbleToProceed(event, () => {
      if (menuItem !== activeMenuItem) {
        dispatch(
          menusSlice.actions.setActiveMenuItem({ activeMenuItem: menuItem }),
        );
        dispatch(
          menusSlice.actions.setActiveNavigationMenuItem({
            activeNavigationMenuItem: '',
          }),
        );
        dispatch(tradingSlice.actions.setCurrentTab(0));
      }
    });
  };

  const handleClickMenuRoot = (menuItem) => {
    if (activeNavigationMenuItem === menuItem) {
      dispatch(
        menusSlice.actions.setActiveNavigationMenuItem({
          activeNavigationMenuItem: '',
        }),
      );
    } else {
      dispatch(
        menusSlice.actions.setActiveNavigationMenuItem({
          activeNavigationMenuItem: menuItem,
        }),
      );
    }
  };

  const getMenuLabel = () => {
    let menuLabelExpression = 'TRLT_UI-ORDERS';

    if (
      activeMenuItem === 'TRADING:BOLSA' ||
      activeMenuItem === 'TRADING:BOLSA:DETAILS'
    ) {
      menuLabelExpression = 'TRLT_UI-ORDERS_EXCHANGE';
    } else if (activeMenuItem === 'TRADING:PRIMARY') {
      menuLabelExpression = 'TRLT_UI-ORDERS_PRIMARY';
    } else if (
      activeMenuItem === 'TRADING:FUNDS' ||
      activeMenuItem === 'TRADING:FUNDS:DETAILS'
    ) {
      menuLabelExpression = 'TRLT_UI-ORDERS_FUNDS';
    }

    return TrlFormattedMessagePlain({ id: menuLabelExpression });
  };

  if (!available) return null;

  return (
    <>
      <DrawerMobileStyled
        open={activeNavigationMenuItem.includes(OPTION_ID)}
        anchor="bottom"
        onClose={(_, reason) =>
          (reason === 'backdropClick' || reason === undefined) &&
          dispatch(
            menusSlice.actions.setActiveNavigationMenuItem({
              activeNavigationMenuItem: '',
            }),
          )
        }
      >
        <ListMobileStyled
          component="div"
          disablePadding
          opened={activeNavigationMenuItem.includes(OPTION_ID)}
          selected={activeMenuItem?.includes(OPTION_ID)}
        >
          {availableStockExchange && (
            <LeafListItemButtonMobileStyled
              onClick={(event) =>
                handleClickLeafMenu(event, `${OPTION_ID}:${LEAF_OPTION_ID2}`)
              }
              component={Link}
              to="/Trading/StockExchange"
              selected={activeMenuItem?.includes(
                `${OPTION_ID}:${LEAF_OPTION_ID2}`,
              )}
              id="btnMenuTradingSecondary"
            >
              <LeafListItemTextMobileStyled
                primary={<FormattedMessage id="TRLT_UI-EXCHANGE" />}
                selected={activeMenuItem?.includes(
                  `${OPTION_ID}:${LEAF_OPTION_ID2}`,
                )}
                isOpen={isOpen}
              />
            </LeafListItemButtonMobileStyled>
          )}
          {availablePrimary && (
            <LeafListItemButtonMobileStyled
              onClick={(event) =>
                handleClickLeafMenu(event, `${OPTION_ID}:${LEAF_OPTION_ID1}`)
              }
              component={Link}
              to="/Trading/Primary"
              selected={activeMenuItem?.includes(
                `${OPTION_ID}:${LEAF_OPTION_ID1}`,
              )}
              id="btnMenuTradingPrimary"
            >
              <LeafListItemTextMobileStyled
                primary={<FormattedMessage id="TRLT_UI-PRIMARY" />}
                selected={activeMenuItem?.includes(
                  `${OPTION_ID}:${LEAF_OPTION_ID1}`,
                )}
                isOpen={isOpen}
              />
            </LeafListItemButtonMobileStyled>
          )}
          {availableFunds && (
            <LeafListItemButtonMobileStyled
              onClick={(event) =>
                handleClickLeafMenu(event, `${OPTION_ID}:${LEAF_OPTION_ID3}`)
              }
              component={Link}
              to="/Trading/Funds"
              selected={activeMenuItem?.includes(
                `${OPTION_ID}:${LEAF_OPTION_ID3}`,
              )}
              id="btnMenuTradingFunds"
            >
              <LeafListItemTextMobileStyled
                primary={<FormattedMessage id="TRLT_UI-FUNDS" />}
                selected={activeMenuItem?.includes(
                  `${OPTION_ID}:${LEAF_OPTION_ID3}`,
                )}
                isOpen={isOpen}
              />
            </LeafListItemButtonMobileStyled>
          )}
        </ListMobileStyled>
      </DrawerMobileStyled>
      <RootListItemButtonMobileStyled
        onClick={() => handleClickMenuRoot(OPTION_ID)}
        selected={activeMenuItem?.includes(OPTION_ID)}
        opened={activeNavigationMenuItem.includes(OPTION_ID)}
        isSubMenuPopup={activeNavigationMenuItem !== ''}
        id="btnMenuTrading"
      >
        <ListItemIconMobileStyled>
          <FontAwesomeIconMobileStyled
            icon={faHandshake}
            selected={activeMenuItem?.includes(OPTION_ID)}
          />
        </ListItemIconMobileStyled>
        <ListItemText primary={getMenuLabel()} />
      </RootListItemButtonMobileStyled>
    </>
  );
};

TradingMenuItemMobile.propTypes = {
  isOpen: PropTypes.bool,
  activeMenuItem: PropTypes.string,
  available: PropTypes.bool,
  availablePrimary: PropTypes.bool,
  availableStockExchange: PropTypes.bool,
  setActiveMenuItem: PropTypes.func,
  activeNavigationMenuItem: PropTypes.string,
  setActiveNavigationMenuItem: PropTypes.func,
  availableFunds: PropTypes.bool,
};
